import { z } from 'zod';
import {
  statusActionListSchema,
  transitionGuardListSchema,
} from './itinerary-status-machine';

/**
 * The declarative operation status process.
 *
 * The same model the itinerary status process uses
 * (`itinerary-status-machine.ts`), applied to operation statuses and kept
 * deliberately INDEPENDENT of it — separate constants, separate stage column,
 * separate router. The two describe different things and will diverge; a shared
 * generic would couple them and make every future change to one a change to
 * both.
 *
 *  1. **A status's position in the process IS its operation stage.** There is
 *     no separate step index: moving a status between stages is the move.
 *  2. **A step may target one status, any status, or any status in a stage.**
 *     A wildcard states "from here you may go anywhere" without enumerating
 *     every status, and stays correct when a new status is added later.
 *  3. **Stages are fixed.** This feature assigns statuses to them and never
 *     invents them.
 *  4. **Stage 0 means "not in the process"**, and a status there may lead only
 *     to a Stage 1 status — those statuses still hold real services, so they
 *     need exactly one way back in.
 */

/** Stage 0 doubles as "not in the process" (decision 4). */
export const UNPLACED_OPERATION_STAGE = 0;
/** The only destination a Stage 0 status may have (decision 4). */
export const REENTRY_OPERATION_STAGE = 1;

/**
 * The operation stages, in one place. Decision 3 says this feature assigns
 * statuses to stages and never invents them, so the set is fixed — and `save`
 * rejects anything outside it rather than quietly writing a stage no screen can
 * show.
 *
 * The values follow the bands the seeded `status_code` ladder already treats as
 * meaningful — S0/S1 requested, S2–S7 in flight, S11/S12 done, >= 20
 * exception/terminal — so the numbers mean the same thing the codes already did.
 * The editor still renders a column for any other stage it finds, so a status
 * can never become invisible; see `operationStageColumns`.
 */
export const OPERATION_STAGES: { value: number; label: string }[] = [
  { value: 0, label: 'Not in the process' },
  { value: 1, label: 'Stage 1 — Requested' },
  { value: 4, label: 'Stage 4 — Awaiting' },
  { value: 10, label: 'Stage 10 — Confirmed' },
  { value: 20, label: 'Stage 20 — Closed' },
];

export const isKnownOperationStage = (operationStage: number): boolean =>
  OPERATION_STAGES.some((s) => s.value === operationStage);

export const operationStageLabel = (operationStage: number): string =>
  OPERATION_STAGES.find((s) => s.value === operationStage)?.label ??
  `Stage ${operationStage}`;

/**
 * Columns to render: the canonical stages, plus any other stage the data
 * actually contains. Deriving the extras is what stops a status at an
 * unexpected stage from vanishing from the board — and, worse, being swept to
 * stage 0 by "Clear" while invisible.
 */
export const operationStageColumns = (
  stagesInUse: readonly (number | null)[]
): { value: number; label: string }[] => {
  const extras = [
    ...new Set(
      stagesInUse.filter(
        (s): s is number => s != null && !isKnownOperationStage(s)
      )
    ),
  ].sort((a, b) => a - b);
  return [
    ...OPERATION_STAGES,
    ...extras.map((value) => ({ value, label: `Stage ${value}` })),
  ];
};

/**
 * The stages the "advance to next status" chain walks, in order.
 *
 * Closed (20) is deliberately absent, and so is Not-in-the-process (0) — the
 * same shape as the Operations card's checkmark, which already excludes the
 * exception and terminal codes from its advance chain.
 */
export const ADVANCING_OPERATION_STAGES: readonly number[] = [1, 4, 10];

// --- Actions and guards --------------------------------------------------

export type {
  StatusAction as OperationStatusAction,
  StatusActionType as OperationStatusActionType,
  TransitionGuard as OperationTransitionGuard,
  TransitionGuardType as OperationTransitionGuardType,
} from './itinerary-status-machine';
/**
 * The action and guard shapes are the ITINERARY flow's, reused verbatim.
 *
 * One library and one log serve both flows (see `statusFlowScopeValues`), so a
 * second set of schemas here would be a second wire format for the same rows —
 * and the two would drift. Which actions an OPERATION status may be given is
 * decided by the catalog's `scope`, not by a separate type union.
 */
export {
  statusActionListSchema as operationStatusActionListSchema,
  statusActionSchema as operationStatusActionSchema,
  statusActionTypeSchema as operationStatusActionTypeSchema,
  transitionGuardListSchema as operationTransitionGuardListSchema,
  transitionGuardSchema as operationTransitionGuardSchema,
  transitionGuardTypeSchema as operationTransitionGuardTypeSchema,
} from './itinerary-status-machine';

// --- Targets (decision 2) ------------------------------------------------

/**
 * Where a step leads. Modelled as a discriminated union rather than a nullable
 * status id, so "any status" is a first-class answer instead of a missing value
 * every reader has to interpret.
 */
export const operationTransitionTargetSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('status'), statusId: z.uuid() }),
  z.object({ kind: z.literal('stage'), operationStage: z.number() }),
  z.object({ kind: z.literal('any') }),
]);
export type OperationTransitionTarget = z.infer<
  typeof operationTransitionTargetSchema
>;

/** Stable key for a target, used to de-duplicate the steps out of one status. */
export const operationTransitionTargetKey = (
  target: OperationTransitionTarget
): string => {
  if (target.kind === 'status') return `status:${target.statusId}`;
  if (target.kind === 'stage') return `stage:${target.operationStage}`;
  return 'any';
};

/**
 * Decision 4, in one place so the editor, the API and the enforcement guard
 * agree: a status outside the process may only lead back to Stage 1.
 */
export const isOperationTargetAllowedFromStage = (
  fromOperationStage: number,
  target: OperationTransitionTarget,
  operationStageOfStatus: (statusId: string) => number | undefined
): boolean => {
  if (fromOperationStage !== UNPLACED_OPERATION_STAGE) return true;
  if (target.kind === 'any') return false;
  if (target.kind === 'stage') {
    return target.operationStage === REENTRY_OPERATION_STAGE;
  }
  return operationStageOfStatus(target.statusId) === REENTRY_OPERATION_STAGE;
};

/**
 * A step may never lead INTO a status that is out of the process.
 *
 * Being out of the process means nothing steps into it: decision 4 lets an
 * unplaced status lead only back to Stage 1, so a step INTO one would be an
 * exit that no configured step could reverse. Kept beside decision 4 rather
 * than folded into it so the editor and the API can report the two separately —
 * they fail for different reasons and deserve different messages.
 *
 * Only an explicit `status` target is checked. The `any` and `stage` wildcards
 * are unaffected: they describe a breadth ("wherever the process allows"), and
 * narrowing what they resolve to is enforcement's job, not the editor's.
 */
export const isOperationTargetInProcess = (
  target: OperationTransitionTarget,
  operationStageOfStatus: (statusId: string) => number | undefined
): boolean =>
  target.kind !== 'status' ||
  operationStageOfStatus(target.statusId) !== UNPLACED_OPERATION_STAGE;

/**
 * The status a step along the advance chain should move a service to: the
 * nominated default of the ADJACENT advancing stage, forward or back.
 *
 * Returns null — meaning "offer nothing" — when there is nowhere sensible to
 * go: the current status is outside the advancing chain, it is already at the
 * end being stepped toward, or the adjacent stage has no default nominated.
 * That last case is why nothing is offered rather than a guess: with several
 * statuses in a stage, picking one would be inventing the process rather than
 * following it.
 *
 * A stage holding exactly ONE status needs no nomination — there is nothing to
 * disambiguate, so that status is its own default.
 *
 * Forward and back are one function because they are one rule read in two
 * directions; two copies would let the Next and Back buttons disagree about
 * what the chain is.
 */
const resolveOperationStatusStep = <
  T extends {
    id: string;
    operationStage: number | null;
    isStageDefault: boolean;
  },
>(
  statuses: readonly T[],
  currentStatusId: string | null,
  direction: 1 | -1
): T | null => {
  if (!currentStatusId) return null;
  const current = statuses.find((s) => s.id === currentStatusId);
  if (!current || current.operationStage == null) return null;

  const index = ADVANCING_OPERATION_STAGES.indexOf(current.operationStage);
  if (index < 0) return null;

  const targetIndex = index + direction;
  if (targetIndex < 0 || targetIndex >= ADVANCING_OPERATION_STAGES.length) {
    return null;
  }

  const targetStage = ADVANCING_OPERATION_STAGES[targetIndex];
  const inTargetStage = statuses.filter(
    (s) => s.operationStage === targetStage
  );
  if (inTargetStage.length === 0) return null;
  if (inTargetStage.length === 1) return inTargetStage[0] ?? null;
  return inTargetStage.find((s) => s.isStageDefault) ?? null;
};

/** The next status along the chain, or null when there is nowhere to advance. */
export const resolveNextOperationStatus = <
  T extends {
    id: string;
    operationStage: number | null;
    isStageDefault: boolean;
  },
>(
  statuses: readonly T[],
  currentStatusId: string | null
): T | null => resolveOperationStatusStep(statuses, currentStatusId, 1);

/**
 * The previous status along the chain, or null when there is nowhere to go
 * back to.
 *
 * Back is a correction, not a reversal of history: it moves the service to the
 * stage before this one, and the log records it like any other change.
 */
export const resolvePreviousOperationStatus = <
  T extends {
    id: string;
    operationStage: number | null;
    isStageDefault: boolean;
  },
>(
  statuses: readonly T[],
  currentStatusId: string | null
): T | null => resolveOperationStatusStep(statuses, currentStatusId, -1);

// --- The machine ---------------------------------------------------------

export const operationStatusMachineStateSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  statusCode: z.string().nullable(),
  statusType: z.string().nullable(),
  color: z.string().nullable(),
  /** Decision 1: the stage IS the position in the process. */
  operationStage: z.number().nullable(),
  /** The status this stage advances to. At most one per stage. */
  isStageDefault: z.boolean(),
  /** Read-only: what the application already does. */
  onEnter: statusActionListSchema,
  onExit: statusActionListSchema,
  /**
   * The actions ATTACHED to this status, which is what the board renders.
   * Distinct from `onEnter` above: that describes what the code does for an
   * organization that has not been migrated, and cannot be changed.
   */
  actions: z
    .array(
      z.object({
        bindingId: z.uuid(),
        actionId: z.uuid(),
        name: z.string(),
        type: z.string(),
        phase: z.string(),
        description: z.string().nullable(),
        isEnabled: z.boolean(),
      })
    )
    .default([]),
  /** Services currently sitting in this state — context for the screen. */
  itemCount: z.number().int().nonnegative(),
});
export type OperationStatusMachineState = z.infer<
  typeof operationStatusMachineStateSchema
>;

export const operationStatusMachineTransitionSchema = z.object({
  id: z.uuid(),
  fromStatusId: z.uuid(),
  target: operationTransitionTargetSchema,
  guards: transitionGuardListSchema,
  /** Actions bound to this EDGE rather than to either status. */
  onTransition: statusActionListSchema,
});
export type OperationStatusMachineTransition = z.infer<
  typeof operationStatusMachineTransitionSchema
>;

export const operationStatusMachineOverviewSchema = z.object({
  states: z.array(operationStatusMachineStateSchema),
  transitions: z.array(operationStatusMachineTransitionSchema),
});
export type OperationStatusMachineOverview = z.infer<
  typeof operationStatusMachineOverviewSchema
>;

/** Steps out of a status, for the editor and the enforcement guard. */
export const operationTransitionsFrom = (
  transitions: readonly OperationStatusMachineTransition[],
  fromStatusId: string
): OperationStatusMachineTransition[] =>
  transitions.filter((t) => t.fromStatusId === fromStatusId);

// --- Writes --------------------------------------------------------------

/**
 * The editor saves the whole graph at once. A diff-based API would have to
 * reconcile concurrent edits of a small, wholly-visible config; replacing it
 * inside one transaction is simpler and matches how the screen is used.
 */
export const saveOperationStatusMachineInput = z.object({
  /**
   * Stage assignments — which column each status sits in, and which of them is
   * the one its stage advances to. Sent for every status the editor shows, so
   * an omitted `isStageDefault` is a real "not the default", not a no-op.
   */
  stages: z.array(
    z.object({
      statusId: z.uuid(),
      operationStage: z.number().int(),
      isStageDefault: z.boolean().default(false),
    })
  ),
  transitions: z.array(
    z.object({
      fromStatusId: z.uuid(),
      target: operationTransitionTargetSchema,
      guards: transitionGuardListSchema.default([]),
      onTransition: statusActionListSchema.default([]),
    })
  ),
});
export type SaveOperationStatusMachineInput = z.infer<
  typeof saveOperationStatusMachineInput
>;
